import { useState, useMemo } from 'react';
import { Player } from '../types/Player';

export type FilterAssessedType = 'all' | 'assessed' | 'unassessed' | 'assessed-by-me' | 'not-assessed-by-me';

/**
 * Custom hook to manage player filtering logic
 * @param players The full list of players to filter
 * @param coachId The ID of the current coach
 * @returns Object containing filtered players and filter state/setters
 */
export function usePlayerFiltering(players: Player[], coachId: string) {
  const [nameSearch, setNameSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [filterAssessed, setFilterAssessed] = useState<FilterAssessedType>('all');

  // Filter logic for the player list using evaluatedByCoachIds
  const filteredPlayers = useMemo(() => {
    return players.filter(player => {
      const fullName = `${player.first_name ?? ''} ${player.last_name ?? ''}`.trim().toLowerCase();
      const nameMatch = fullName.includes(nameSearch.toLowerCase());
      const positions = (player.profile?.positions ?? []);
      const positionMatch = positionFilter === '' || positions.some(pos => pos === positionFilter);

      const coachIdsWhoEvaluated = player.evaluatedByCoachIds || [];
      const hasAnyEvaluation = coachIdsWhoEvaluated.length > 0;
      const hasMyEvaluation = coachIdsWhoEvaluated.includes(coachId);

      switch (filterAssessed) {
        case 'assessed': // Assessed by anyone
          return nameMatch && positionMatch && hasAnyEvaluation;
        case 'unassessed': // Not assessed by anyone
          return nameMatch && positionMatch && !hasAnyEvaluation;
        case 'assessed-by-me':
          return nameMatch && positionMatch && hasMyEvaluation;
        case 'not-assessed-by-me':
          return nameMatch && positionMatch && !hasMyEvaluation;
        default: // 'all'
          return nameMatch && positionMatch;
      }
    });
  }, [players, nameSearch, positionFilter, filterAssessed, coachId]);

  return {
    filteredPlayers,
    nameSearch,
    setNameSearch,
    positionFilter,
    setPositionFilter,
    filterAssessed,
    setFilterAssessed,
    // isEvaluationComplete removed
  };
}